import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { MapPinPlus, Loader2, CheckCircle2, Clock, ImagePlus, X } from "lucide-react";

const EMPTY = { name: "", address: "", city: "Charlotte, NC", courts_count: "", surface: "", website: "", notes: "" };

const STATUS_STYLE = {
  pending: { icon: Clock, cls: "bg-[#F5B700]/15 text-[#8a6d00]", label: "Pending review" },
  approved: { icon: CheckCircle2, cls: "bg-[#005F19]/10 text-[#005F19]", label: "Approved" },
  rejected: { icon: Clock, cls: "bg-[#0E395F]/10 text-[#0E395F]", label: "Not added" },
};

export default function SubmitCourtDialog() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [isPublic, setIsPublic] = useState(false);
  const [minCourts, setMinCourts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mine, setMine] = useState([]);
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoRef = useRef(null);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB.");
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/court-submissions/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoUrl(data.url);
      toast.success("Photo added");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploadingPhoto(false);
      if (photoRef.current) photoRef.current.value = "";
    }
  };

  const loadMine = async () => {
    try {
      const { data } = await http.get("/court-submissions/mine");
      setMine(data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadMine(); }, []);

  const submit = async () => {
    if (!form.name.trim() || !form.address.trim()) return toast.error("Court name and address are required.");
    const count = parseInt(form.courts_count, 10);
    if (!count || count < 2) return toast.error("A venue must have at least 2 courts.");
    if (!minCourts) return toast.error("Please confirm the venue has at least 2 courts.");
    if (!isPublic) return toast.error("Please confirm the courts are open to the public.");
    setSaving(true);
    try {
      await http.post("/court-submissions", {
        name: form.name.trim(),
        address: form.address.trim(),
        city: form.city.trim() || "Charlotte, NC",
        courts_count: count,
        is_public: true,
        surface: form.surface.trim() || null,
        website: form.website.trim() || null,
        notes: form.notes.trim() || null,
        photo_url: photoUrl || null,
      });
      toast.success("Court submitted — thanks for growing the PaddleVerse!");
      setForm(EMPTY); setIsPublic(false); setMinCourts(false); setPhotoUrl("");
      setOpen(false);
      loadMine();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-3xl bg-white border border-black/5 shadow-sm p-6 sm:p-8 mt-6" data-testid="submit-court-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-black pv-text-dark flex items-center gap-2"><MapPinPlus size={20} className="pv-text-support" /> Submit a New Court</h2>
          <p className="text-sm text-black/50 mt-1 max-w-md">Know a great spot we're missing? Send it in and we'll review it for the Court Radar.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="open-submit-court-btn" className="pv-bg-support text-white font-bold rounded-full px-5 hover:opacity-90">
              <MapPinPlus size={16} className="mr-2" /> Submit a Court
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="submit-court-dialog">
            <DialogHeader>
              <DialogTitle>Submit a new court</DialogTitle>
              <DialogDescription>Help fellow players discover more places to play. Submissions are reviewed before going live.</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-1">
              <div className="space-y-2">
                <Label htmlFor="c-name">Venue name *</Label>
                <Input id="c-name" data-testid="court-name-input" value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder="e.g. Veterans Park Pickleball Courts" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-address">Address *</Label>
                <Input id="c-address" data-testid="court-address-input" value={form.address} onChange={(e) => set("address")(e.target.value)} placeholder="Street, City, State" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="c-city">City</Label>
                  <Input id="c-city" data-testid="court-city-input" value={form.city} onChange={(e) => set("city")(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-count">Number of courts *</Label>
                  <Input id="c-count" data-testid="court-count-input" type="number" min="2" value={form.courts_count} onChange={(e) => set("courts_count")(e.target.value)} placeholder="2+" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="c-surface">Surface (optional)</Label>
                  <Input id="c-surface" data-testid="court-surface-input" value={form.surface} onChange={(e) => set("surface")(e.target.value)} placeholder="Concrete, cushioned…" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-web">Website (optional)</Label>
                  <Input id="c-web" data-testid="court-website-input" value={form.website} onChange={(e) => set("website")(e.target.value)} placeholder="https://" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-notes">Notes (optional)</Label>
                <Textarea id="c-notes" data-testid="court-notes-input" rows={3} value={form.notes} onChange={(e) => set("notes")(e.target.value)} placeholder="Hours, lights, reservation info, anything helpful…" />
              </div>

              <div className="space-y-2">
                <Label>Photo (optional)</Label>
                <input ref={photoRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" data-testid="court-photo-input" onChange={handlePhoto} />
                {photoUrl ? (
                  <div className="relative w-full h-40 rounded-xl overflow-hidden border border-black/10" data-testid="court-photo-preview">
                    <img src={photoUrl} alt="Court preview" className="w-full h-full object-cover" />
                    <button type="button" data-testid="court-photo-remove" onClick={() => setPhotoUrl("")} className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors">
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <button type="button" data-testid="court-photo-trigger" onClick={() => photoRef.current?.click()} disabled={uploadingPhoto} className="w-full h-24 rounded-xl border-2 border-dashed border-black/15 grid place-items-center text-black/50 hover:border-[#4A90E2] hover:text-[#4A90E2] transition-colors disabled:opacity-60">
                    {uploadingPhoto ? <Loader2 size={20} className="animate-spin" /> : <span className="flex flex-col items-center gap-1 text-sm font-semibold"><ImagePlus size={22} /> Add a photo of the courts</span>}
                  </button>
                )}
              </div>

              <div className="rounded-xl bg-[#F8F6F3] border border-black/5 p-4 space-y-3">
                <div className="text-xs font-bold uppercase tracking-wider text-black/40">Submission requirements</div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox data-testid="court-mincourts-check" checked={minCourts} onCheckedChange={(v) => setMinCourts(!!v)} className="mt-0.5" />
                  <span className="text-sm text-black/70">This venue has <b>at least 2 dedicated courts</b>.</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox data-testid="court-public-check" checked={isPublic} onCheckedChange={(v) => setIsPublic(!!v)} className="mt-0.5" />
                  <span className="text-sm text-black/70">These courts are <b>open to the public</b> (not private/members-only).</span>
                </label>
              </div>
            </div>

            <DialogFooter>
              <Button data-testid="submit-court-btn" onClick={submit} disabled={saving} className="pv-bg-primary text-black font-bold rounded-full px-6 hover:opacity-90">
                {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <MapPinPlus size={16} className="mr-2" />} Submit for review
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {mine.length > 0 && (
        <div className="mt-6 space-y-2" data-testid="my-court-submissions">
          <div className="text-xs font-bold uppercase tracking-wider text-black/40">Your submissions</div>
          {mine.map((s) => {
            const st = STATUS_STYLE[s.status] || STATUS_STYLE.pending;
            const Icon = st.icon;
            return (
              <div key={s.submission_id} data-testid="court-submission-item" className="flex items-center justify-between gap-3 rounded-xl border border-black/5 bg-white px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  {s.photo_url && <img src={s.photo_url} alt={s.name} className="h-11 w-11 rounded-lg object-cover shrink-0" data-testid="court-submission-thumb" />}
                  <div className="min-w-0">
                    <div className="font-bold pv-text-dark text-sm truncate">{s.name}</div>
                    <div className="text-xs text-black/50 truncate">{s.address} · {s.courts_count} courts</div>
                  </div>
                </div>
                <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${st.cls}`}>
                  <Icon size={12} /> {st.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
